<template>
  <div class="tasks-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Task Board</h1>
        <p class="page-subtitle">{{ filteredTasks.length }} task{{ filteredTasks.length !== 1 ? 's' : '' }} · sorted by priority</p>
      </div>
      <button class="btn btn-primary" @click="showCreateModal = true">
        <span>＋</span> New Task
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <button
        v-for="f in filters"
        :key="f.value"
        class="filter-btn"
        :class="{ active: activeFilter === f.value }"
        @click="activeFilter = f.value; fetchTasks()"
      >
        <span class="filter-dot" :class="f.value"></span>
        {{ f.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <span>Loading tasks…</span>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredTasks.length === 0" class="state-box empty">
      <div class="empty-icon">◫</div>
      <p class="empty-title">No tasks found</p>
      <p class="empty-sub">{{ activeFilter !== 'all' ? 'No tasks with this status.' : 'Create your first task to get started.' }}</p>
      <button v-if="activeFilter === 'all'" class="btn btn-primary" @click="showCreateModal = true">Create Task</button>
    </div>

    <!-- Task List -->
    <div v-else class="task-list">
      <TaskCard
        v-for="task in filteredTasks"
        :key="task.id"
        :task="task"
        @update-status="handleUpdateStatus"
        @delete="handleDelete"
      />
    </div>

    <!-- Create Modal -->
    <CreateTaskModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="onTaskCreated"
    />

    <!-- Toast Notifications -->
    <div class="toast-container">
      <div v-for="t in toasts" :key="t.id" class="toast" :class="`toast-${t.type}`">
        {{ t.message }}
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import TaskCard from '@/components/TaskCard.vue'
import CreateTaskModal from '@/components/CreateTaskModal.vue'

export default {
  name: 'TasksView',
  components: { TaskCard, CreateTaskModal },
  data() {
    return {
      tasks: [],
      loading: false,
      activeFilter: 'all',
      showCreateModal: false,
      toasts: [],
      filters: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Done', value: 'done' }
      ]
    }
  },
  computed: {
    filteredTasks() {
      return this.tasks
    }
  },
  mounted() {
    this.fetchTasks()
  },
  methods: {
    async fetchTasks() {
      this.loading = true
      try {
        const status = this.activeFilter !== 'all' ? this.activeFilter : null
        const res = await api.getTasks(status)
        this.tasks = res.data.data || res.data
      } catch {
        this.toast('Failed to load tasks', 'error')
      } finally {
        this.loading = false
      }
    },
    async handleUpdateStatus({ id, newStatus }) {
      try {
        await api.updateStatus(id, newStatus)
        this.toast('Status updated!', 'success')
        this.fetchTasks()
      } catch (err) {
        const msg = err.response?.data?.message || 'Could not update status'
        this.toast(msg, 'error')
      }
    },
    async handleDelete(id) {
      try {
        await api.deleteTask(id)
        this.toast('Task deleted.', 'success')
        this.fetchTasks()
      } catch (err) {
        const msg = err.response?.data?.message || 'Only completed tasks can be deleted'
        this.toast(msg, 'error')
      }
    },
    onTaskCreated() {
      this.showCreateModal = false
      this.toast('Task created!', 'success')
      this.fetchTasks()
    },
    toast(message, type = 'success') {
      const id = Date.now()
      this.toasts.push({ id, message, type })
      setTimeout(() => {
        this.toasts = this.toasts.filter(t => t.id !== id)
      }, 3500)
    }
  }
}
</script>

<style scoped>
.tasks-page { animation: fadeIn 0.3s ease; }

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.page-title {
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 2rem;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, var(--text) 0%, var(--text-muted) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle { color: var(--text-muted); font-size: 0.875rem; margin-top: 4px; }

.filter-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.filter-btn:hover { border-color: var(--text-muted); color: var(--text); }
.filter-btn.active { border-color: var(--accent); color: var(--accent); background: rgba(79,142,247,0.08); }

.filter-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-muted);
}
.filter-dot.pending { background: var(--text-muted); }
.filter-dot.in_progress { background: var(--accent2); }
.filter-dot.done { background: var(--success); }

.task-list { display: flex; flex-direction: column; gap: 10px; }

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4rem 2rem;
  color: var(--text-muted);
  text-align: center;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.empty-icon { font-size: 3rem; opacity: 0.2; }
.empty-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 1.1rem; color: var(--text); }
.empty-sub { font-size: 0.875rem; max-width: 280px; }

@keyframes spin { to { transform: rotate(360deg) } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
</style>